<template>
  <div class="kanban-board">
    <div class="kanban-columns">
      <div v-for="status in statuses" :key="status.value" class="kanban-column">
        <div class="kanban-column-header d-flex justify-content-between align-items-center px-3 py-2" :style="{ borderTop: '4px solid ' + status.color, backgroundColor: 'white' }">
          <h6 class="mb-0 fw-bold text-uppercase small">{{ $t(status.value) }}</h6>
          <span class="badge rounded-pill bg-light text-dark border">{{ getPlanningsByStatus(status.value).length }}</span>
        </div>
        <div 
          class="kanban-column-body px-2 py-3"
          @drop="onDrop($event, status.value)"
          @dragover="onDragOver($event)"
          @dragenter="onDragEnter($event)"
          @dragleave="onDragLeave($event)"
        >
          <div 
            v-for="planning in getPlanningsByStatus(status.value)" 
            :key="planning.id" 
            class="kanban-card mb-3 p-3 bg-white shadow-sm border rounded-3"
            :draggable="true"
            @dragstart="onDragStart($event, planning)"
            @dragend="onDragEnd($event)"
            @click="onCardClick(planning)"
          >
            <!-- Priority & Time -->
            <div class="d-flex justify-content-end align-items-center mb-2">
              <small class="text-muted" style="font-size: 10px;">
                <AppIcon name="bi-clock" class="bi bi-clock me-1" />
                {{ planning.start_time || '--:--' }}
              </small>
            </div>

            <!-- Title -->
            <h6 class="planning-title fw-bold mb-1">{{ planning.title }}</h6>
            
            <!-- Client Info -->
            <div v-if="planning.client" class="mb-2">
              <small class="text-primary fw-medium">
                <AppIcon name="bi-person" class="bi bi-person me-1" />
                {{ planning.client.name }}
              </small>
            </div>

            <!-- Address -->
            <div v-if="planning.address" class="mb-2">
              <small class="text-muted d-block text-truncate">
                <AppIcon name="bi-geo-alt" class="bi bi-geo-alt me-1" />
                {{ planning.address }}
              </small>
            </div>

            <!-- Divider -->
            <hr class="my-2 opacity-25">

            <!-- Footer Actions -->
            <div class="d-flex justify-content-between align-items-center">
              <div class="d-flex gap-1">
                <button 
                  @click.stop="$emit('update-status', planning, getNextStatus(planning.status))" 
                  class="btn btn-xs btn-outline-primary"
                  v-if="getNextStatus(planning.status)"
                  :title="$t('move_to_next_status')"
                >
                  <AppIcon :name="$i18n.locale === 'ar' ? 'bi bi-arrow-left' : 'bi bi-arrow-right'" :class="$i18n.locale === 'ar' ? 'bi bi-arrow-left' : 'bi bi-arrow-right'" />
                </button>
              </div>
              <small class="text-muted" style="font-size: 10px;">
                {{ formatDate(planning.date) }}
              </small>
            </div>
          </div>

          <!-- Empty State for Column -->
          <div v-if="!getPlanningsByStatus(status.value).length" class="text-center text-muted py-4 small opacity-50">
            <AppIcon name="bi-inbox" class="bi bi-inbox d-block fs-4 mb-1" />
            {{$t('no_planning')}}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import helper from '../../helper'

export default {
  name: 'MyPlanningKanban',
  props: {
    plannings: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      helper,
      draggedItem: null,
      statuses: [
        { value: 'planned', color: '#0dcaf0' },
        { value: 'in_progress', color: '#0d6efd' },
        { value: 'completed', color: '#198754' },
        { value: 'failed', color: '#dc3545' },
        { value: 'postponed', color: '#ffc107' }
      ]
    }
  },
  methods: {
    getPlanningsByStatus(status) {
      return this.plannings.filter(p => p.status === status)
    },
    getPriorityColor(priority) {
      const colors = {
        low: 'secondary',
        medium: 'primary',
        high: 'warning',
        urgent: 'danger'
      }
      return colors[priority] || 'primary'
    },
    getNextStatus(currentStatus) {
      const statusFlow = {
        'planned': 'in_progress',
        'in_progress': 'completed',
        'postponed': 'planned'
      }
      return statusFlow[currentStatus] || null
    },
    formatDate(date) {
      return helper.formatDate(date)
    },
    onCardClick(planning) {
      // Don't open detail if user was dragging
      if (this.wasDragging) {
        this.wasDragging = false
        return
      }
      this.$emit('open-detail', planning)
    },

    // Drag and Drop
    onDragStart(event, planning) {
      this.draggedItem = planning
      event.dataTransfer.effectAllowed = 'move'
      event.target.classList.add('dragging')
    },
    onDragEnd(event) {
      event.target.classList.remove('dragging')
    },
    onDragOver(event) {
      event.preventDefault()
      event.dataTransfer.dropEffect = 'move'
    },
    onDragEnter(event) {
      if (event.target.classList.contains('kanban-column-body')) {
        event.target.classList.add('drag-over')
      }
    },
    onDragLeave(event) {
      if (event.target.classList.contains('kanban-column-body')) {
        event.target.classList.remove('drag-over')
      }
    },
    onDrop(event, newStatus) {
      event.preventDefault()
      event.target.classList.remove('drag-over')
      
      if (!this.draggedItem) return
      
      const oldStatus = this.draggedItem.status
      if (oldStatus !== newStatus) {
        this.$emit('update-status', this.draggedItem, newStatus)
      }
      this.draggedItem = null
    }
  }
}
</script>

<style scoped>
.kanban-board {
  overflow-x: auto;
  padding-bottom: 20px;
  min-height: calc(100vh - 250px);
}

.kanban-columns {
  display: flex;
  gap: 1rem;
  padding: 0.5rem;
}

.kanban-column {
  flex: 0 0 300px;
  background-color: #f1f3f5;
  border-radius: 0.75rem;
  display: flex;
  flex-direction: column;
}

.kanban-column-header {
  border-radius: 0.75rem 0.75rem 0 0;
  box-shadow: 0 2px 4px rgba(0,0,0,0.02);
}

.kanban-column-body {
  flex-grow: 1;
  min-height: 200px;
  transition: background-color 0.2s;
  border-radius: 0 0 0.75rem 0.75rem;
}

.kanban-column-body.drag-over {
  background-color: rgba(13, 110, 253, 0.05);
  border: 2px dashed #0d6efd;
}

.kanban-card {
  cursor: grab;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(0,0,0,0.05) !important;
}

.kanban-card:hover {
  border-color: rgba(13, 110, 253, 0.2) !important;
}

.kanban-card:active {
  cursor: grabbing;
}

.kanban-card.dragging {
  opacity: 0.4;
  transform: scale(0.95);
}

.planning-title {
  font-size: 0.95rem;
  line-height: 1.4;
  color: #2d3436;
}

.btn-xs {
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  line-height: 1;
  border-radius: 0.375rem;
}

/* Scrollbar Styling */
.kanban-board::-webkit-scrollbar {
  height: 8px;
}
.kanban-board::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}
.kanban-board::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 10px;
}
.kanban-board::-webkit-scrollbar-thumb:hover {
  background: #adb5bd;
}
</style>
